import React, { useState, useEffect } from "react";
import FirebaseService from "../../services/FirebaseService";

const DragDropBuilder = () => {
  const [userInfoData, setUserInfoData] = useState("");
  const [elements, setElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [mouseOffset, setMouseOffset] = useState({ x: 0, y: 0 });
  const [lines, setLines] = useState({ vertical: null, horizontal: null });
  const [hoveredElement, setHoveredElement] = useState(null);

  // Fetch user info and set elements on component mount
  useEffect(() => {
    const fetchUserInfo = async () => {
      const uid = localStorage.getItem("uid");
      const { data } = await FirebaseService.get(uid);
      if (data && data[0]) {
        setElements(initializeElements(data[0]));
        setUserInfoData(data[0]);
      }
    };

    fetchUserInfo();
  }, []);

  // Initialize elements with user data or default values
 

  const initializeElements = (userInfoData) => {
    // Map over the user data to handle elements dynamically based on their type
    return (userInfoData.user_data || [])
      .map((element) => {
        switch (element.type) {
          case "profileImage":
            return {
              id: element.id || 1, // ID from data or fallback to 1
              type: "profileImage",
              x: element.x || 50, // Fallback to default x
              y: element.y || 50, // Fallback to default y
              customizations: {
                profileImage: userInfoData.photo_url || "",
                backgroundColor: element.backgroundColor || "#ffffff", // Add customization fallback
              },
            };

          case "text":
            return {
              id: element.id || 2, // ID from data or fallback to 2
              type: "text",
              x: element.x || 200, // Fallback to default x
              y: element.y || 50, // Fallback to default y
              customizations: {
                text: userInfoData.display_name || "Your Name",
                textColor: element.textColor || "#000000",
                fontSize: element.fontSize || "18px",
              },
            };

          case "socialLink":
            return {
              id: element.id || 3, // ID from data or fallback
              type: "socialLink",
              x: element.x || 100, // Fallback to default x
              y: element.y || 150, // Fallback to default y
              customizations: {
                text: element.label || "Social Link",
                link: element.url || "",
                backgroundColor: element.backgroundColor || "#dddddd",
              },
            };

          default:
            return null; // Skip unknown element types
        }
      })
      .filter(Boolean); // Filter out null elements
  };

  // Function to update elements in Firebase
  const updateUserInfo = async (updatedElements) => {
    const uid = localStorage.getItem("uid");
    const updatedData = {
      elements: updatedElements,
    };
    await FirebaseService.update(uid, updatedData);
  };

  const saveElements = () => {
    localStorage.setItem("elements", JSON.stringify(elements));
    updateUserInfo(elements); // Call the update function here
  };

  const addElement = (type) => {
    const newElement = {
      id: elements.length + 1,
      type: type,
      x: 0,
      y: 0,
      customizations: {
        text: type === "button" || type === "text" ? "Sample Text" : "",
        link: type === "socialLink" ? "https://example.com" : "",
        profileImage:
          type === "profileImage" ? userInfoData.photo_url || "" : "",
        backgroundColor: "#ff0000",
        textColor: "#000",
        fontSize: "16px",
        backgroundImage: "",
      },
    };
    const updatedElements = [...elements, newElement];
    setElements(updatedElements);
    updateUserInfo(updatedElements); // Update Firebase when a new element is added
  };

  const onMouseDown = (e, index) => {
    const element = elements[index];
    setSelectedElement(index);
    setMouseOffset({ x: e.clientX - element.x, y: e.clientY - element.y });
    setDragging(true);
  };

  const onMouseMove = (e) => {
    if (!dragging || selectedElement === null) return;
    const newElements = [...elements];
    const updatedElement = newElements[selectedElement];
    const newX = e.clientX - mouseOffset.x;
    const newY = e.clientY - mouseOffset.y;

    updatedElement.x = newX;
    updatedElement.y = newY;

    // Show center/edge lines based on proximity
    showLines(newX, newY);

    setElements(newElements);
  };

  const onMouseUp = () => {
    setDragging(false);
    setSelectedElement(null);
    setLines({ vertical: null, horizontal: null }); // Hide lines when not dragging
    updateUserInfo(elements); // Update Firebase when element dragging is done
  };

  const showLines = (x, y) => {
    const canvas = document.querySelector(".builder-area");
    const canvasCenterX = canvas.offsetWidth / 2;
    const canvasCenterY = canvas.offsetHeight / 2;
    const tolerance = 20; // Pixels within which to show lines

    const newLines = {
      vertical: Math.abs(x - canvasCenterX) < tolerance ? canvasCenterX : null,
      horizontal:
        Math.abs(y - canvasCenterY) < tolerance ? canvasCenterY : null,
    };

    setLines(newLines);
  };

  const handleEditClick = (index) => {
    const updatedElements = [...elements];
    const element = updatedElements[index];
    const newText = prompt("Edit text:", element.customizations.text);
    if (newText !== null) {
      element.customizations.text = newText;
      setElements(updatedElements);
      updateUserInfo(updatedElements); // Update Firebase when text is edited
    }
  };

  const renderElement = (el, index) => {
    const style = {
      position: "absolute",
      left: `${el.x}px`,
      top: `${el.y}px`,
      backgroundColor: el.customizations.backgroundColor || "#ff0000",
      color: el.customizations.textColor || "#000",
      fontSize: el.customizations.fontSize || "16px",
      backgroundImage: `url(${el.customizations.backgroundImage || ""})`,
      backgroundSize: "cover",
    };

    return (
      <div
        key={el.id}
        onMouseDown={(e) => onMouseDown(e, index)}
        onMouseEnter={() => setHoveredElement(index)}
        onMouseLeave={() => setHoveredElement(null)}
        style={style}
        className="cursor-move"
      >
        {el.type === "button" && (
          <div className="bg-blue-500 py-2 px-4 rounded">
            {el.customizations.text || "Default Button Text"}
          </div>
        )}
        {el.type === "profileImage" && (
          <img
            src={el.customizations.profileImage || ""}
            alt="Profile"
            className="w-20 h-20 rounded-full"
          />
        )}
        {el.type === "socialLink" && (
          <div className="bg-gray-800 py-2 px-4 rounded">
            {el.customizations.link || "https://example.com"}
          </div>
        )}
        {el.type === "text" && (
          <div>{el.customizations.text || "Sample Text"}</div>
        )}

        {hoveredElement === index && (
          <div
            className="absolute top-0 right-0 p-1 bg-gray-700 text-white rounded cursor-pointer"
            onClick={() => handleEditClick(index)}
          >
            ✏️
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="flex h-screen"
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
    >
      {/* Left Side: Element Adder */}
      <div className="w-3/4 bg-gray-200 p-4">
        <h3 className="font-bold mb-2">Add Elements</h3>
        <button
          className="bg-blue-500 py-2 px-4 mb-2"
          onClick={() => addElement("button")}
        >
          Add Button
        </button>
        <button
          className="bg-green-500 py-2 px-4 mb-2"
          onClick={() => addElement("profileImage")}
        >
          Add Profile Image
        </button>
        <button
          className="bg-yellow-500 py-2 px-4 mb-2"
          onClick={() => addElement("socialLink")}
        >
          Add Social Link
        </button>
        <button
          className="bg-purple-500 py-2 px-4 mb-2"
          onClick={() => addElement("text")}
        >
          Add Text
        </button>
        <button className="bg-green-500 py-2 px-4 mt-2" onClick={saveElements}>
          Save Elements
        </button>
      </div>

      {/* Right Side: Builder Area */}
      <div className="builder-area w-1/4 bg-gray-100 relative p-4">
        {elements.map((el, index) => renderElement(el, index))}

        {/* Guiding Lines */}
        {lines.vertical && (
          <div
            style={{
              position: "absolute",
              left: `${lines.vertical}px`,
              top: 0,
              height: "100%",
              width: "2px",
              backgroundColor: "red",
            }}
          />
        )}
        {lines.horizontal && (
          <div
            style={{
              position: "absolute",
              top: `${lines.horizontal}px`,
              left: 0,
              width: "100%",
              height: "2px",
              backgroundColor: "red",
            }}
          />
        )}
      </div>
    </div>
  );
};

export default DragDropBuilder;
